import { useCallback, useRef, useState } from 'react';
import type { PersistedReviewStateV1 } from '@/lib/state/importReviewStore';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseManualSaveOptions {
  batchId: string | null;
  getReviewState: () => PersistedReviewStateV1;
  saveFn: (input: {
    batchId: string;
    reviewState: PersistedReviewStateV1;
  }) => Promise<{ success: boolean; savedAt: string }>;
  onSaving?: () => void;
  onSaved?: (savedAt: string) => void;
  onError?: () => void;
}

export function useManualSave({
  batchId,
  getReviewState,
  saveFn,
  onSaving,
  onSaved,
  onError,
}: UseManualSaveOptions) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;
  const getReviewStateRef = useRef(getReviewState);
  getReviewStateRef.current = getReviewState;
  const onSavingRef = useRef(onSaving);
  onSavingRef.current = onSaving;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const save = useCallback(async () => {
    if (!batchId) return;
    const reviewState = getReviewStateRef.current();

    if (Object.keys(reviewState.transactionOverrides).length === 0) return;

    onSavingRef.current?.();
    setStatus('saving');
    try {
      const result = await saveFnRef.current({
        batchId,
        reviewState,
      });
      setStatus('saved');
      setLastSavedAt(result.savedAt);
      onSavedRef.current?.(result.savedAt);
    } catch {
      setStatus('error');
      onErrorRef.current?.();
    }
  }, [batchId]);

  return { status, lastSavedAt, save };
}
